/**
 * LE SUIVI CLIENT — lot 162, 04/11/2026 (eCMR, bêta).
 *
 * La page que le client ouvre par son lien, SANS COMPTE. Elle lit
 * `GET /suivi/:code` (`apps/api/src/liens_suivi.ts`), qui ne rend que ce que
 * la fonction `app.suivi_client` choisit de montrer : ni prix, ni chauffeur,
 * ni camion (décisions du 04/11).
 *
 * VOLONTAIREMENT À PART DE `api.ts` : cette page n'a ni session ni jeton, et
 * elle ne doit embarquer aucun morceau de l'application de l'exploitation.
 */

import { configuration } from './configuration.js';
import {
  lignesChargees,
  quantiteDite,
  quantitesLivrees,
  remplirLettre,
  type ContenuDeLettre,
  type ExecutionArret,
  type ExecutionDeLettre,
} from './lettreVoiture.js';

export interface EtapeSuivie {
  /** Lot 164 : pour rattacher les lettres à leurs étapes. */
  readonly id?: string;
  readonly type: 'chargement' | 'livraison';
  readonly nom: string | null;
  readonly adresse: string | null;
  readonly code_postal: string | null;
  readonly ville: string | null;
  readonly jour: string | null;
  readonly debut: string | null;
  readonly fin: string | null;
  readonly reference: string | null;
  readonly quantite: string | number | null;
  /** Lot 163 : posés par le chauffeur. */
  readonly arrive_le?: string | null;
  readonly parti_le?: string | null;
  /** Lot 164 : la signature et les réserves (« transporteur », jamais le nom du chauffeur). */
  readonly signe_le?: string | null;
  readonly signataire?: string | null;
  readonly reserve_transporteur?: string | null;
  readonly reserve_client?: string | null;
}

/** Une lettre de voiture, montrée au client dès la signature au chargement (lot 164). */
export interface LettreSuivie {
  readonly numero: string;
  readonly generee_le: string;
  readonly etape_chargement_id: string;
  readonly etape_livraison_id: string;
  readonly contenu: ContenuDeLettre;
  /** Lot 164b : la vraie lettre, avec le chauffeur et les plaques de la case 11. */
  readonly chauffeur?: string | null;
  readonly plaques?: { readonly tracteur: string; readonly remorque: string | null } | null;
  readonly chargement: ExecutionArret | null;
  readonly livraison: ExecutionArret | null;
}

export interface Suivi {
  readonly ot: string;
  readonly reference_client: string | null;
  readonly annule: boolean;
  readonly expire_le: string;
  readonly etapes: readonly EtapeSuivie[];
  readonly lettres?: readonly LettreSuivie[];
}

export type LectureSuivi =
  | { readonly etat: 'ok'; readonly suivi: Suivi }
  | { readonly etat: 'inconnu' }
  | { readonly etat: 'injoignable' };

/** Le code est dans l'ancre de l'adresse (`suivi.html#<code>`) : il ne part dans aucun journal de serveur. */
export function codeDeLAdresse(hash: string): string {
  return hash.replace(/^#/, '').trim();
}

export async function lireLeSuivi(code: string): Promise<LectureSuivi> {
  if (!/^[A-Za-z0-9_-]{43}$/.test(code)) return { etat: 'inconnu' };
  let r: Response;
  try {
    r = await fetch(`${configuration.apiUrl}/suivi/${code}`);
  } catch {
    return { etat: 'injoignable' };
  }
  if (r.status === 404) return { etat: 'inconnu' };
  if (!r.ok) return { etat: 'injoignable' };
  return { etat: 'ok', suivi: (await r.json()) as Suivi };
}

/** « 2026-11-06 » → « 06/11 ». */
export function jourCourt(iso: string | null): string {
  if (iso === null) return '';
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

/** « 06/11 · 06:00–08:00 ». */
export function creneau(e: EtapeSuivie): string {
  // « 11:30–11:30 » se lit « 11:30 » (retour de Mohamed, 04/11 16 h 30).
  const h = e.debut === null ? '' : e.fin === null || e.fin === e.debut ? e.debut : `${e.debut}–${e.fin}`;
  return [jourCourt(e.jour), h].filter((x) => x !== '').join(' · ');
}

/** « 45400 Fleury-les-Aubrais » ; la ville seule pour la frise. */
export function villeDe(e: EtapeSuivie): string {
  return e.ville ?? '';
}

/** 16 → « 16 », « 20.00 » → « 20 ». */
export function quantiteDe(q: string | number | null): string {
  if (q === null) return '—';
  const n = Number(q);
  return Number.isFinite(n) ? n.toLocaleString('fr-FR', { maximumFractionDigits: 2 }) : String(q);
}

/** L'heure de Paris d'un horodatage : « 08:12 ». */
export function heureDe(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return '';
  return new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}

/** « 05/11 08:12 », à l'heure de Paris. */
export function momentDe(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return '—';
  const d = new Date(iso);
  const jour = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit' }).format(d);
  return `${jour} ${heureDe(iso)}`;
}

export type EtatEtape = 'fait' | 'encours' | 'planifie';

/** Une étape est faite quand on en est reparti, en cours quand on y est arrivé. */
export function etatEtape(e: EtapeSuivie): EtatEtape {
  if (e.parti_le !== null && e.parti_le !== undefined) return 'fait';
  if (e.arrive_le !== null && e.arrive_le !== undefined) return 'encours';
  return 'planifie';
}

/**
 * L'ÉTAT EN GROS (maquette du 04/11) : Planifié · Chargement en cours ·
 * En transit · Livraison en cours · Livré. Les heures viennent du chauffeur
 * (lot 163).
 */
export function etatDuSuivi(s: Suivi): { readonly titre: string; readonly detail: string } {
  if (s.annule) return { titre: 'Annulé', detail: 'Cette expédition a été annulée.' };
  const premier = s.etapes[0];
  if (premier === undefined) return { titre: 'Planifié', detail: '' };
  const courante = s.etapes.find((e) => etatEtape(e) !== 'fait');
  if (courante === undefined) {
    const derniere = s.etapes[s.etapes.length - 1];
    const reserves = s.etapes.some((e) => reserveDeLEtape(s, e) !== '');
    return { titre: reserves ? 'Livré avec réserves' : 'Livré', detail: `Dernière livraison le ${momentDe(derniere?.parti_le)}` };
  }
  const lieu = `${courante.nom ?? ''}${courante.ville !== null && courante.ville !== '' ? `, ${courante.ville}` : ''}`;
  if (etatEtape(courante) === 'encours') {
    return {
      titre: courante.type === 'chargement' ? 'Chargement en cours' : 'Livraison en cours',
      detail: `Arrivée à ${heureDe(courante.arrive_le)} · ${lieu}`,
    };
  }
  if (courante === premier) {
    return { titre: 'Planifié', detail: `Enlèvement prévu le ${creneau(courante)} · ${courante.nom ?? ''}`.trim() };
  }
  return { titre: 'En transit', detail: `Prochaine ${courante.type === 'chargement' ? 'étape' : 'livraison'} : ${lieu} · ${creneau(courante)}` };
}

// ---------------------------------------------------------------------------
// Lot 164 : réserves et lettres

/**
 * Ce que remplit la lettre côté client : LA VRAIE LETTRE, chauffeur et plaques
 * compris (Mohamed, 04/11 17 h 21). La page, elle, ne les montre pas.
 */
export function executionPourLeClient(l: LettreSuivie): ExecutionDeLettre {
  return { chauffeur: l.chauffeur ?? null, plaques: l.plaques ?? null, chargement: l.chargement, livraison: l.livraison };
}

/**
 * LA RÉSERVE D'UNE ÉTAPE, comme sur la lettre : « Transporteur : … —
 * Destinataire : … », puis, à une livraison, chaque ligne livrée en moins.
 */
export function reserveDeLEtape(s: Suivi, e: EtapeSuivie): string {
  if (e.signe_le === null || e.signe_le === undefined) return '';
  const qui = e.type === 'chargement' ? 'Expéditeur' : 'Destinataire';
  const r: string[] = [];
  if (e.reserve_transporteur) r.push(`Transporteur : ${e.reserve_transporteur}`);
  if (e.reserve_client) r.push(`${qui} : ${e.reserve_client}`);
  if (e.type === 'livraison') {
    for (const l of (s.lettres ?? []).filter((x) => x.etape_livraison_id === e.id)) {
      const ex = executionPourLeClient(l);
      const livrees = quantitesLivrees(l, ex);
      if (livrees === null) continue;
      lignesChargees(l, ex).forEach((x, i) => {
        const q = livrees[i];
        if (q !== undefined && q !== Number(x.quantite)) {
          r.push(`${x.support ?? ''} : ${quantiteDite(String(q))} sur ${quantiteDite(x.quantite)}`);
        }
      });
    }
  }
  return r.join(' — ');
}

/** L'état d'une lettre, côté client (maquette du 04/11). */
export function etatDeLettreSuivie(l: LettreSuivie): { readonly classe: '' | 'ok' | 'obs'; readonly texte: string } {
  const liv = l.livraison;
  if (liv !== null && liv.signe_le !== null) {
    const ex = executionPourLeClient(l);
    const livrees = quantitesLivrees(l, ex) ?? [];
    const manque = lignesChargees(l, ex).some((x, i) => livrees[i] !== undefined && livrees[i] !== Number(x.quantite));
    return liv.reserve_client !== null || liv.reserve_chauffeur !== null || manque
      ? { classe: 'obs', texte: 'Livrée — réserves' }
      : { classe: 'ok', texte: 'Livrée' };
  }
  return { classe: '', texte: 'Signée à l’enlèvement' };
}

/**
 * LE PDF : la lettre seule, à la taille A4, dans une page à part qui
 * s'imprime d'elle-même — « Enregistrer au format PDF » du navigateur.
 */
export function pageImprimable(l: LettreSuivie): string {
  return `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Lettre de voiture ${l.numero}</title>
<style>@page{size:A4;margin:0}html,body{margin:0;background:#fff}.sig-off{visibility:hidden}</style></head>
<body>${remplirLettre({ ...l, id: l.numero, generee_par: '' }, executionPourLeClient(l))}
<script>window.addEventListener('load',function(){setTimeout(function(){window.print()},300)})</script></body></html>`;
}
